import { useCallback } from 'react'
import type { MutationHook } from '@commerce/utils/types'
import { CommerceError } from '@commerce/utils/errors'
import useAddLine, { UseAddLine } from '@commerce/actual-cart/use-add-line'
import useCart from './use-cart'

import {
  cartCreate,
  cartLinesAddMutation,
  cartToCart,
  getCartId,
} from '../utils'
import { AttributeInput, Maybe, Mutation, Scalars} from '../schema'

import { handler as useCustomerHandler } from '../customer/use-customer';




/** Specifies the input fields to create a line item on a checkout. */
export type CartLineInput = {
  /** Extra information in the form of an array of Key-Value pairs about the line item. */
  customAttributes?: Maybe<Array<AttributeInput>>
  /** The identifier of the product variant for the line item. */
  merchandiseId: Scalars['ID']
  /** The quantity of the line item. */
  quantity: Scalars['Int']
  /** The identifier of the selling plan for the line item. */
  sellingPlanId?: Scalars['ID']
}

/** The schema’s entry-point for mutations. This acts as the public, top-level API from which all mutation queries must start. */
export type MutationCartLinesAddArgs = {
  lines: Array<CartLineInput>
  cartId: Scalars['ID']
}




export default useAddLine as UseAddLine<typeof handler>

export const handler: MutationHook<any> = {
  fetchOptions: {
    query: cartLinesAddMutation,
  },
  async fetcher({ input: item, options, fetch }) {
    if (
      item.quantity &&
      (!Number.isInteger(item.quantity) || item.quantity! < 1)
    ) {
      throw new CommerceError({
        message: 'The item quantity has to be a valid integer greater than 0',
      })
    }

    let cartId;
    if (item?.createSeparateCart === true || getCartId() == null) {
      let customer;
      if (useCustomerHandler?.fetcher != null) {
        customer = await useCustomerHandler.fetcher({
          options: useCustomerHandler.fetchOptions,
          input: undefined,
          fetch,
        });
      }

      const cart = await cartCreate(fetch, true, customer);
      cartId = cart?.id;
    } else {
      cartId = getCartId();
    }

    let lines: any = []
    if(item.merchandiseId.constructor === Array) {
      item.merchandiseId.map((i: any) => {
        let line: any = {}
        line.merchandiseId = i
        line.quantity = item.quantity ?? 1
        line.attributes = item.attributes ? [...item.attributes] : []
        if(item.sellingPlanId) {
          line.sellingPlanId = item.sellingPlanId
          line.attributes.push({
            key: '_sellingPlanId',
            value: item.sellingPlanId
          })
        }
        lines.push(line)
      })
    } else {
      let line: any = {}
      line.merchandiseId = item.merchandiseId
      line.quantity = item.quantity ?? 1
      line.attributes = item.attributes ? [...item.attributes] : []
      if(item.sellingPlanId) {
        line.sellingPlanId = item.sellingPlanId
        line.attributes.push({
          key: '_sellingPlanId',
          value: item.sellingPlanId
        })
      }
      lines.push(line)
    }

    const { cartLinesAdd } = await fetch<
      Mutation,
      MutationCartLinesAddArgs
    >({
      ...options,
      variables: {
        cartId: cartId,
        lines: lines,
      },
    }) as { cartLinesAdd: any }
    return cartToCart(cartLinesAdd)
  },
  useHook: ({ fetch }) => () => {
    const { mutate } = useCart()

    return useCallback(
      async function addItem(input) {
        const data = await fetch({ input })
        await mutate(data, false)
        return data
      },
      [fetch, mutate]
    )
  },
}
